import { destinationFor, droppableId } from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useDroppable } from "@dnd-kit/core";
import React from "react";

/**
 * Drop target for attaching files to the home/workspace composer, whose chat
 * may not exist yet (`ChatComposerDropTarget` needs a `flowSessionId`; this
 * surface mints one on drop via the registered `ComposerAttachTarget`).
 *
 * Deliberately wraps ONLY the composer input — the page around it keeps its
 * own upload/move drop targets, so dropping on the input attaches to the
 * message while dropping anywhere else uploads into the workspace. dnd-kit's
 * deepest-target collision resolution keeps the two from fighting.
 */
export function ComposerAttachDropTarget({
  scopeKey,
  occludes,
  children,
  className,
}: {
  /**
   * Names both the droppable id and the registered `ComposerAttachTarget`
   * that receives the drop — workspace id or "home" for page composers,
   * `floating:<scope>` for the floating chat window.
   */
  scopeKey: string;
  /**
   * Set when this surface floats over other droppables (see
   * `DroppableData.occludes`) so covered targets can't steal the drop.
   */
  occludes?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId.composerAttach(scopeKey),
    data: {
      surface: "composer-attach" as const,
      destination: destinationFor("composer-attach", { scopeKey }),
      occludes,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={cnTransferDropTargetSurface({ isOver, className })}
    >
      {children}
    </div>
  );
}
